import axios, { AxiosResponse, AxiosError } from 'axios';
import { store } from '../store/store';
import { clearAuth } from '../store/slices/authSlice';
import { addNotification } from '../store/slices/uiSlice';

// API Configuration
// Production-safe: Uses REACT_APP_API_URL if set, otherwise intelligent fallback
const getApiBaseUrl = () => {
  // If explicitly set in env, use that (production should always set this)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Fallback: Determine API URL based on current environment
  const currentHost = window.location.hostname;
  const currentProtocol = window.location.protocol;
  const isProduction = currentProtocol === 'https:' || 
                       (!currentHost.includes('localhost') && 
                        !currentHost.includes('127.0.0.1'));
  
  if (isProduction) {
    // Production fallback: Use same hostname with /api path
    // This assumes frontend and backend are on the same domain
    // WARNING: This should not happen if REACT_APP_API_URL is properly configured
    console.warn('REACT_APP_API_URL not set in production! Using fallback. Please set REACT_APP_API_URL environment variable.');
    return `${currentProtocol}//${currentHost}/api`;
  }
  
  // Development: Prefer localhost for cookie compatibility
  // If frontend is on 127.0.0.1, still use localhost for API (they're the same machine)
  // This ensures cookies work properly with SameSite='Lax'
  const apiHost = currentHost === '127.0.0.1' ? 'localhost' : currentHost;
  const apiPort = '8000';
  
  // Use localhost for API (or current hostname if not 127.0.0.1)
  return `http://${apiHost}:${apiPort}/api`;
};

const API_BASE_URL = getApiBaseUrl();

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable cookies to be sent with requests
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Ensure cookies are sent with all requests
    config.withCredentials = true;
    
    // Skip ngrok browser warning page (only when using ngrok)
    if (API_BASE_URL.includes('ngrok')) {
      config.headers['ngrok-skip-browser-warning'] = 'true';
    }
    
    // Note: Tokens are now in httpOnly cookies, which are sent automatically
    // Authorization header is no longer needed for cookie-based authentication
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Refresh token is now in httpOnly cookie, so we just call the refresh endpoint
      // The backend will read the refresh token from the cookie
      try {
        await axios.post(
          `${API_BASE_URL}/auth/refresh/`,
          {},
          { withCredentials: true } // Ensure cookies are sent
        );
        
        // New access token is now in httpOnly cookie
        // We don't need to store it in Redux anymore
        // Just retry the original request - cookies will be sent automatically
        originalRequest.withCredentials = true;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Check if we're currently authenticating or if this is an auth-related request
        const authState = store.getState().auth;
        const isAuthRequest = originalRequest.url?.includes('/auth/') || false;
        
        // Don't show "Session Expired" notification during login flow
        if (!authState.loading && !isAuthRequest) {
          // Refresh failed, logout user
          store.dispatch(clearAuth());
          store.dispatch(addNotification({
            type: 'error',
            title: 'Session Expired',
            message: 'Please log in again.',
          }));
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// API Response Types
interface APIResponse<T> {
  success: boolean;
  message: string;
  data: T;
  error?: {
    type: string;
    message: string;
    details: any;
  };
}

// Authentication API
// Phone validation API
export const phoneValidationAPI = {
  /**
   * Validate phone number using backend validation
   * @param phone - Phone number to validate
   * @param countryCode - ISO country code (e.g., 'JO', 'US', 'SA')
   * @returns Validation result with formatted number and detected country
   */
  validatePhone: async (phone: string, countryCode: string = 'JO') => {
    try {
      const response = await apiClient.post('/validate-phone/', {
        phone: phone.trim(),
        country_code: countryCode
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Validation failed',
        data: error.response?.data || null
      };
    }
  }
};

export const authAPI = {
  // Health check
  healthCheck: (): Promise<AxiosResponse<APIResponse<any>>> => 
    apiClient.get('/health/'),

  // Login with email (Admin/Host)
  loginWithEmail: (credentials: { email: string; password: string; rememberMe?: boolean }): Promise<AxiosResponse<APIResponse<any>>> => {
    return apiClient.post('/auth/login/', {
      email: credentials.email,
      password: credentials.password,
      remember_me: credentials.rememberMe || false
    }).then(response => {
      return response;
    }).catch(error => {
      throw error;
    });
  },

  // Register host
  registerHost: (hostData: {
    email: string;
    phone: string;
    password: string;
    password_confirm: string;
    first_name: string;
    last_name: string;
  }): Promise<AxiosResponse<APIResponse<any>>> =>
    apiClient.post('/auth/register/host/', hostData),

  // Request OTP for guest
  requestOTP: (guestData: { phone: string; country_code?: string }): Promise<AxiosResponse<APIResponse<any>>> =>
    apiClient.post('/auth/otp/send/', guestData),

  // Verify OTP
  verifyOTP: (otpData: { phone: string; otp_code: string; country_code?: string; remember_me?: boolean }): Promise<AxiosResponse<APIResponse<any>>> =>
    apiClient.post('/auth/otp/verify/', otpData),

  // Request OTP for admin/host
  requestAdminHostOTP: (adminHostData: { phone: string; country_code?: string }): Promise<AxiosResponse<APIResponse<any>>> =>
    apiClient.post('/auth/admin-host/otp/send/', adminHostData),

  // Verify OTP for admin/host
  verifyAdminHostOTP: (otpData: { phone: string; otp_code: string; country_code?: string; remember_me?: boolean; login_as_role?: string }): Promise<AxiosResponse<APIResponse<any>>> =>
    apiClient.post('/auth/admin-host/otp/verify/', otpData),

  // Refresh token (refresh token is now in httpOnly cookie)
  refreshToken: (): Promise<AxiosResponse<APIResponse<any>>> =>
    apiClient.post('/auth/refresh/', {}),

  // Logout
  logout: (allDevices: boolean = false): Promise<AxiosResponse<APIResponse<any>>> =>
    apiClient.post('/auth/logout/', { all_devices: allDevices }),

  // Get user profile
  getProfile: (): Promise<AxiosResponse<APIResponse<any>>> =>
    apiClient.get('/profile/'),

  // Update user profile
  updateProfile: (profileData: any): Promise<AxiosResponse<APIResponse<any>>> =>
    apiClient.put('/profile/', profileData),

  // Change password
  changePassword: (passwordData: {
    current_password: string;
    new_password: string;
    new_password_confirm: string;
  }): Promise<AxiosResponse<APIResponse<any>>> =>
    apiClient.post('/auth/change-password/', passwordData),

  // Request password reset OTP
  requestPasswordReset: (resetData: {
    phone: string;
    country_code?: string;
  }): Promise<AxiosResponse<APIResponse<any>>> =>
    apiClient.post('/auth/password-reset/request/', resetData),

  // Verify password reset OTP only (without resetting password)
  verifyPasswordResetOTP: (verifyData: {
    phone: string;
    country_code?: string;
    otp_code: string;
  }): Promise<AxiosResponse<APIResponse<any>>> =>
    apiClient.post('/auth/password-reset/verify-otp/', verifyData),

  // Verify password reset OTP and reset password
  verifyPasswordReset: (verifyData: {
    phone: string;
    country_code?: string;
    otp_code: string;
    new_password: string;
    new_password_confirm: string;
  }): Promise<AxiosResponse<APIResponse<any>>> =>
    apiClient.post('/auth/password-reset/verify/', verifyData),
};

// Export the axios instance for custom requests
export default apiClient;
